import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Download, Upload, Save, Loader2, BellOff, Mail, Smartphone, Zap } from "lucide-react";

const CANALI = [
  { value: "email", label: "Solo Email", icon: Mail },
  { value: "push", label: "Solo Push", icon: Smartphone },
  { value: "entrambi", label: "Email + Push", icon: Zap },
];

/**
 * Iter122k99 · Console tenant per Automazioni Notifiche.
 * Permette di modificare per ogni automazione: ON/OFF, canale, giorni, orario,
 * silenzio notturno/festivi, cap settimana. Export/Import via XLSX.
 */
export default function AutomazioniNotificheCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savingRow, setSavingRow] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/automazioni-notifiche");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRow = (code, patch) => {
    setRows((old) => old.map((r) => (r.code === code ? { ...r, ...patch } : r)));
  };

  const saveRow = async (row) => {
    setSavingRow(row.code);
    try {
      const payload = {
        attivo: row.attivo,
        canale: row.canale,
        ora_invio: row.ora_invio,
        silenzio_notturno: row.silenzio_notturno,
        silenzio_festivi: row.silenzio_festivi,
        max_settimana: Number(row.max_settimana ?? 0),
        ...(row.frequenza_giorni !== undefined && { frequenza_giorni: Number(row.frequenza_giorni) }),
        ...(row.frequenza_dopo !== undefined && { frequenza_dopo: Number(row.frequenza_dopo) }),
        ...(row.giorni_soglie !== undefined && { giorni_soglie: parseArr(row.giorni_soglie) }),
        ...(row.giorni_pre_scadenza !== undefined && { giorni_pre_scadenza: Number(row.giorni_pre_scadenza) }),
        ...(row.giorni_post_scadenza !== undefined && { giorni_post_scadenza: parseArr(row.giorni_post_scadenza) }),
        ...(row.delay_ore !== undefined && { delay_ore: Number(row.delay_ore) }),
      };
      const r = await api.put(`/automazioni-notifiche/${row.code}`, payload);
      updateRow(row.code, r.data);
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingRow(null); }
  };

  const parseArr = (v) => {
    if (Array.isArray(v)) return v.map(Number).filter((n) => !isNaN(n));
    return String(v || "").split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
  };

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const resp = await fetch(`${backend}/api/automazioni-notifiche/export.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "automazioni-notifiche.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File esportato");
    } catch (e) {
      toast.error(e.message || "Errore export");
    } finally { setBusy(false); }
  };

  const importXlsx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/automazioni-notifiche/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data;
      toast.success(`Import completato: ${d.updated.length} aggiornate, ${d.skipped.length} saltate`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Caricamento…</div>;

  return (
    <div data-testid="automazioni-notifiche-card" className="space-y-5">
      <Card className="p-4 bg-amber-50 border-amber-200 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <BellOff className="w-5 h-5 shrink-0" />
          <div>
            <b>Regola le notifiche automatiche</b> inviate a fornitori e condòmini.
            I default sono già tarati per essere <b>meno invasivi</b> di prima
            (soglie diluite, silenzio notturno 20:00-08:00, no domenica/festivi, cap 2 solleciti/settimana).
            Puoi esportare in Excel, modificare i valori e re-importare il file.
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="btn-export-xlsx" onClick={exportXlsx} disabled={busy} variant="outline" className="gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Esporta Excel
        </Button>
        <Button data-testid="btn-import-xlsx" onClick={() => fileInputRef.current?.click()}
          disabled={busy} variant="outline" className="gap-2">
          <Upload className="w-4 h-4" /> Importa Excel
        </Button>
        <input ref={fileInputRef} type="file" accept=".xlsx" onChange={importXlsx} className="hidden" />
      </div>

      <div className="space-y-4">
        {rows.map((row) => (
          <AutomazioneRow
            key={row.code}
            row={row}
            onChange={(patch) => updateRow(row.code, patch)}
            onSave={() => saveRow(row)}
            saving={savingRow === row.code}
          />
        ))}
      </div>
    </div>
  );
}

function AutomazioneRow({ row, onChange, onSave, saving }) {
  const canale = CANALI.find((c) => c.value === row.canale) || CANALI[2];
  return (
    <Card data-testid={`row-${row.code}`} className={`p-4 border-2 ${row.attivo ? "" : "opacity-60 bg-slate-50"}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-brand font-bold">{row.sezione}</div>
          <div className="font-semibold text-slate-800 mt-0.5">{row.descrizione}</div>
          <div className="text-xs text-slate-400 mt-0.5">{row.code}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-semibold">{row.attivo ? "Attiva" : "Disattiva"}</span>
          <Switch data-testid={`switch-${row.code}`} checked={!!row.attivo}
            onCheckedChange={(v) => onChange({ attivo: v })} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600">Canale</label>
          <Select value={row.canale} onValueChange={(v) => onChange({ canale: v })}>
            <SelectTrigger data-testid={`canale-${row.code}`} className="h-10 mt-1">
              <SelectValue><canale.icon className="w-4 h-4 inline mr-1" />{canale.label}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {CANALI.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  <c.icon className="w-4 h-4 inline mr-1" />{c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600">Ora invio (indicativa)</label>
          <Input data-testid={`ora-${row.code}`} type="time" value={row.ora_invio || ""}
            onChange={(e) => onChange({ ora_invio: e.target.value })} className="h-10 mt-1" />
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600">Max solleciti / settimana per destinatario</label>
          <Input data-testid={`cap-${row.code}`} type="number" min="0" max="20"
            value={row.max_settimana ?? 0}
            onChange={(e) => onChange({ max_settimana: Number(e.target.value) })}
            className="h-10 mt-1" />
        </div>

        {row.giorni_soglie !== undefined && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Giorni-soglia (es. 3,7,14)</label>
            <Input data-testid={`soglie-${row.code}`} value={Array.isArray(row.giorni_soglie) ? row.giorni_soglie.join(",") : row.giorni_soglie}
              onChange={(e) => onChange({ giorni_soglie: e.target.value })}
              className="h-10 mt-1" />
          </div>
        )}
        {row.frequenza_dopo !== undefined && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Frequenza dopo l'ultima soglia (giorni)</label>
            <Input data-testid={`freqdopo-${row.code}`} type="number" min="1" max="90"
              value={row.frequenza_dopo}
              onChange={(e) => onChange({ frequenza_dopo: Number(e.target.value) })}
              className="h-10 mt-1" />
          </div>
        )}
        {row.frequenza_giorni !== undefined && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Frequenza (giorni tra rinvii)</label>
            <Input data-testid={`freq-${row.code}`} type="number" min="1" max="60"
              value={row.frequenza_giorni}
              onChange={(e) => onChange({ frequenza_giorni: Number(e.target.value) })}
              className="h-10 mt-1" />
          </div>
        )}
        {row.giorni_pre_scadenza !== undefined && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Giorni pre-scadenza</label>
            <Input data-testid={`pre-${row.code}`} type="number" min="0" max="30"
              value={row.giorni_pre_scadenza}
              onChange={(e) => onChange({ giorni_pre_scadenza: Number(e.target.value) })}
              className="h-10 mt-1" />
          </div>
        )}
        {row.giorni_post_scadenza !== undefined && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Giorni post-scadenza (es. 3,10,20)</label>
            <Input data-testid={`post-${row.code}`} value={Array.isArray(row.giorni_post_scadenza) ? row.giorni_post_scadenza.join(",") : row.giorni_post_scadenza}
              onChange={(e) => onChange({ giorni_post_scadenza: e.target.value })}
              className="h-10 mt-1" />
          </div>
        )}
        {row.delay_ore !== undefined && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Ritardo email chat (ore)</label>
            <Input data-testid={`delay-${row.code}`} type="number" min="0" max="48"
              value={row.delay_ore}
              onChange={(e) => onChange({ delay_ore: Number(e.target.value) })}
              className="h-10 mt-1" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t">
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid={`silnotte-${row.code}`} checked={!!row.silenzio_notturno}
            onCheckedChange={(v) => onChange({ silenzio_notturno: v })} />
          <span>Silenzio notturno (20:00 – 08:00, no push)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid={`silfest-${row.code}`} checked={!!row.silenzio_festivi}
            onCheckedChange={(v) => onChange({ silenzio_festivi: v })} />
          <span>Silenzio festivi (domenica + festività IT)</span>
        </label>
        <Button data-testid={`save-${row.code}`} onClick={onSave} disabled={saving}
          className="ml-auto gap-2 bg-brand hover:bg-brand-dark">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
        </Button>
      </div>
    </Card>
  );
}
